/**
 * RezervaceModal — create or change one reservation (P3-b). The car is a fleet entry and the customer the firma's
 * own `zakaznici` row: nothing is copied here, the form only points at them. A technical reservation is an
 * internal block (no customer). The availability guard is the server's: a collision comes back as a 409 with the
 * colliding reservations, which the form lists; a `pujcovna:admin` may then book through it explicitly.
 * Same freeze-while-saving and no-dismiss-while-pending rules as the other fleet modals.
 */

import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from '@tanstack/react-router';
import { Button } from '@/components/Button';
import { Field, Fields } from '@/components/Field';
import { LINK_BUTTON_CLS, LinkButton } from '@/components/LinkButton';
import { useModuleAccess } from '@/lib/permissions';
import { Modal, DialogCrown } from '@/components/Modal';
import { SearchInput } from '@/components/SearchInput';
import { SegmentedControl } from '@/components/SegmentedControl';
import { SelectInput } from '@/components/SelectInput';
import { TextInput } from '@/components/TextInput';
import { toast } from '@/components/Toaster';
import { ApiError } from '@/lib/api';
import { useDebouncedValue } from '@/lib/use-debounced-value';
import { CenaPanel } from './CenaPanel';
import { PlatbyPanel } from './PlatbyPanel';
import { RezervaceDostupnost } from './RezervaceDostupnost';
import { servisPrekazky, zacatekChyba } from './dostupnost-helpers';
import { cenaBody, kauceSeed, kauceZFormulare, slevaSeed, slevaZFormulare, stejnaSleva, type SlevaForm } from './ceny-helpers';
import { addDays, pragueToday } from '@/lib/prague-date';
import { useCustomerPicker } from '@/modules/zakaznici/api';
import {
  useCenaQuery,
  useDostupnostQuery,
  usePojistovnyQuery,
  useSaveRezervaceMutation,
  type FleetEntry,
  type PujcovnaPobocka,
  type Rezervace,
  type RezervaceKonflikt,
} from './api';
import {
  formatTermin,
  fromInputValue,
  jeBlokace,
  jeFormularZmenen,
  stredisko as STREDISKA,
  strediskoKUlozeni,
  toInputValue,
  vozidloLabel,
  type RezervaceStredisko,
  type RezervaceTyp,
} from './rezervace-helpers';

interface RezervaceModalProps {
  /** null = a new reservation. */
  rezervace: Rezervace | null;
  fleet: FleetEntry[];
  pobocky: PujcovnaPobocka[];
  /** `pujcovna:admin`: may book through a collision. */
  canOverride: boolean;
  /** Why the form only shows the reservation (no right to change it, the car left the fleet); undefined = editable. A returned rental is always shown frozen. */
  jenCteni?: string;
  /** Hand the car over (P4-d): present on an active reservation that is still in the yard; closes this form first. */
  onPredat?: () => void;
  /** Take the car back (P4-e): present on a rental that is out; closes this form first. */
  onPrijmout?: () => void;
  /** Preselected car of a new reservation (a car's row action, the calendar). */
  defaultVozidlo?: number | null;
  /** Preselected pickup of a new reservation (`YYYY-MM-DD`, the calendar's clicked day). */
  defaultOd?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RezervaceModal({
  rezervace,
  fleet,
  pobocky,
  canOverride,
  jenCteni,
  onPredat,
  onPrijmout,
  defaultVozidlo = null,
  defaultOd = null,
  open,
  onOpenChange,
}: RezervaceModalProps) {
  const save = useSaveRezervaceMutation();
  const editing = rezervace != null;
  const startDay = defaultOd ?? pragueToday();
  const initialCar = rezervace?.id_vozidla ?? defaultVozidlo ?? null;

  const [idVozidla, setIdVozidla] = useState<number | null>(initialCar);
  const [idPobocky, setIdPobocky] = useState<number | null>(
    rezervace?.id_pobocky ?? fleet.find((f) => f.id === initialCar)?.id_pobocky ?? pobocky[0]?.id ?? null,
  );
  const [typ, setTyp] = useState<RezervaceTyp>(rezervace?.typ ?? 'denni');
  // an internal block (technical, or the workshop's `servis`): no customer, no price, no payments
  const blokace = jeBlokace(typ);
  const [stredisko, setStredisko] = useState<RezervaceStredisko>(rezervace?.stredisko ?? 'pujcovna');
  const [idPojistovny, setIdPojistovny] = useState<number | null>(rezervace?.id_pojistovny ?? null);
  const [od, setOd] = useState(rezervace ? toInputValue(rezervace.od) : `${startDay}T10:00`);
  const [do_, setDo] = useState(rezervace ? toInputValue(rezervace.do) : `${addDays(startDay, 1)}T10:00`);
  const [zakaznik, setZakaznik] = useState<{ id: number; nazev: string } | null>(
    rezervace?.id_zakaznika != null ? { id: rezervace.id_zakaznika, nazev: rezervace.zakaznik_nazev ?? `#${rezervace.id_zakaznika}` } : null,
  );
  const [poznamka, setPoznamka] = useState(rezervace?.poznamka ?? '');
  const [zahranici, setZahranici] = useState(rezervace?.cena_vstup.zahranici ?? false);
  const [sleva, setSleva] = useState<SlevaForm>(() => slevaSeed(rezervace));
  // the deposit of this rental: empty = the car's own
  const [kauce, setKauce] = useState(() => kauceSeed(rezervace));
  // The collisions of the REJECTED car and term: cleared when the car or the dates change, so "Rezervovat přesto"
  // can only ever override the collisions the operator has just seen.
  const [konflikty, setKonflikty] = useState<RezervaceKonflikt[] | null>(null);
  // The servis events the server refused the term for (the car is in the workshop); like the collisions, they belong to the rejected car and term.
  const [servisServer, setServisServer] = useState<Array<{ id_udalosti: number; popis: string }> | null>(null);
  // The turnaround gap in force, as the server reported it with the collision.
  const [mezera, setMezera] = useState(0);
  const [search, setSearch] = useState('');
  const debounced = useDebouncedValue(search, 300);
  // a renter who is not in the evidence yet is created on the customer form (the full page, in its own tab: this form stays as it is)
  const access = useModuleAccess();
  const smiZalozitZakaznika = access.can('zakaznici', 'write');
  const customers = useCustomerPicker(debounced, { enabled: !blokace && zakaznik == null, pageSize: 6 });
  // a branch payment of the block below is a call of its own; it too keeps the form open until it is answered
  const [platbaBezi, setPlatbaBezi] = useState(false);
  const busy = save.isPending;
  // Once the car is handed over only the END (later) and the note change (the server's rule, mirrored here so the
  // frozen controls say so instead of failing at the save).
  const vraceno = Boolean(rezervace?.vraceno_at);
  const predano = Boolean(rezervace?.predano_at) && !vraceno;
  const zamknuto = vraceno || Boolean(jenCteni);
  const zamceno = busy || predano || zamknuto;
  // The car of a saved reservation may be changed while the SERVER says so (not handed over, no money taken, not a servis replacement car):
  // the reason comes from it, the form never re-implements the rule.
  const vozidloZamceno = editing && !rezervace.lze_zmenit_vozidlo;
  const vozidloZmeneno = editing && idVozidla !== rezervace.id_vozidla;
  const vozidloHint = vozidloZmeneno
    ? 'Změna vozidla přepočítá cenu — nové vozidlo se zkontroluje na kolize a servis.'
    : vozidloZamceno && !zamknuto && !predano
      ? rezervace.duvod_zmeny_vozidla
      : undefined;
  const vozidloHintTon = vozidloZmeneno ? 'warn' : 'neutral';
  // The handover opens another dialog and drops this form: never over edits that were not saved.
  const slevaParsed = slevaZFormulare(sleva);
  const kauceParsed = kauceZFormulare(kauce);
  // a daily "Servis — hradí pojišťovna" rental is billed to an insurer; every other kind has none
  const jePojistovna = typ === 'denni' && stredisko === 'servis_pojistovna';
  /** "Servis — hradí servis": the server prices the lease at 0 (only the deposit is left), so abroad and a discount change nothing. */
  const hradiServis = typ === 'denni' && stredisko === 'servis_servis';
  const pojId = jePojistovna ? idPojistovny : null;
  // a technical block has no středisko to choose: the select is hidden and the saved value comes from the type
  const strediskoUlozene = strediskoKUlozeni(typ, stredisko, rezervace);
  const pojistovny = usePojistovnyQuery(jePojistovna);
  const rozepsano =
    rezervace != null &&
    jeFormularZmenen(rezervace, {
      idVozidla, idPobocky, typ, stredisko: strediskoUlozene, od, do: do_, idZakaznika: zakaznik?.id ?? null, poznamka, idPojistovny: pojId,
      // the price inputs are part of what the handover would silently discard
      zahranici, sleva: slevaParsed.ok ? slevaParsed.sleva : 'chyba', kauce: kauceParsed.ok ? kauceParsed.kauce : 'chyba',
    });

  // The price: the frozen one while nothing that decides it has changed, otherwise the server's quote for the form as it is (debounced).
  const dotazNyni =
    !blokace && idVozidla != null && fromInputValue(do_) > fromInputValue(od) && slevaParsed.ok && kauceParsed.ok
      ? {
          id_vozidla: idVozidla,
          typ,
          stredisko,
          od: fromInputValue(od),
          do: fromInputValue(do_),
          id_zakaznika: zakaznik?.id ?? null,
          id_pojistovny: pojId,
          zahranici,
          sleva: slevaParsed.sleva,
          kauce: kauceParsed.kauce,
        }
      : null;
  const dotaz = useDebouncedValue(dotazNyni, 350);
  // Within the debounce window `dotaz` still describes the PREVIOUS form, so its quote must not pass for the price of what is on screen
  // (nor be what a quick save is judged against): the quote is held back, and the save waits those few hundred ms.
  const dotazZastaral = JSON.stringify(dotaz) !== JSON.stringify(dotazNyni);
  // what decides the price, as the reservation holds it
  const stejnyZaklad =
    rezervace != null &&
    rezervace.id_vozidla === idVozidla &&
    rezervace.typ === typ &&
    rezervace.stredisko === stredisko &&
    toInputValue(rezervace.od) === od &&
    toInputValue(rezervace.do) === do_ &&
    (rezervace.id_zakaznika ?? null) === (zakaznik?.id ?? null) &&
    (rezervace.id_pojistovny ?? null) === pojId &&
    slevaParsed.ok &&
    kauceParsed.ok;
  const cenaBezeZmeny =
    stejnyZaklad && rezervace.cena != null && rezervace.cena_vstup.zahranici === zahranici && stejnaSleva(slevaParsed.sleva, rezervace.cena_vstup.sleva) && kauceParsed.kauce === (rezervace.cena_vstup.kauce ?? null);
  // A rental that went out before prices existed keeps no price whatever is changed (the server refuses to invent one after the fact).
  const bezCenyPoPredani = rezervace != null && rezervace.cena == null && (predano || vraceno);
  // A booking whose price was decided at save and came out empty (a car without a sale price...) stays unpriced until its terms change; one
  // from before the price gets a price when it is saved, and the quote shows which.
  const dosudBezCeny = rezervace != null && rezervace.cena_k_dopocitani;
  const nechanoBezCeny = stejnyZaklad && rezervace.cena == null && !rezervace.cena_k_dopocitani && !bezCenyPoPredani && rezervace.cena_vstup.zahranici === zahranici && stejnaSleva(slevaParsed.sleva, rezervace.cena_vstup.sleva) && kauceParsed.kauce === (rezervace.cena_vstup.kauce ?? null);
  const nabidka = useCenaQuery(cenaBezeZmeny || bezCenyPoPredani || nechanoBezCeny ? null : dotaz);
  // ... and until the quote of the current form has ARRIVED (a slow request must not let the operator save a price they never saw);
  // a failed quote does not hold the save back — the server prices it anyway and the panel says the quote failed
  const cenaCeka = (dotazZastaral || nabidka.isFetching) && !cenaBezeZmeny && !blokace;

  // A new reservation (or a moved start) cannot begin on a day that has passed — the way the servis planner refuses a
  // past slot; today is fine, a rental is judged by calendar day.
  const dnes = pragueToday();
  const zacatekProblem = zamknuto ? null : zacatekChyba(od, dnes, rezervace ? rezervace.od : null);

  // The car's other reservations around the term, with the collisions the server's guard would find (an advisory read,
  // debounced; the save re-checks under the car's lock). It asks about the whole term WITH its times, since the server's guard
  // judges by time: a block that ends the morning the rental starts is no collision.
  const dostupnostNyni =
    idVozidla != null && od.length >= 16 && do_.length >= 16 && do_ >= od
      ? { id_vozidla: idVozidla, od: od.slice(0, 16).replace('T', ' '), do: do_.slice(0, 16).replace('T', ' '), ...(rezervace ? { ignore: rezervace.id } : {}) }
      : null;
  const dostupnostDotaz = useDebouncedValue(dostupnostNyni, 300);
  const dostupnost = useDostupnostQuery(zamknuto ? null : dostupnostDotaz);
  // collisions count only for the answer to the CURRENT form (not a placeholder of the previous term, not a debounce lag)
  const dostupnostPlatna =
    dostupnost.isSuccess && !dostupnost.isPlaceholderData && JSON.stringify(dostupnostDotaz) === JSON.stringify(dostupnostNyni);
  const predbezneKonflikty: RezervaceKonflikt[] = dostupnostPlatna
    ? dostupnost.data.rezervace.filter((r) => r.konflikt).map(({ id, od: a, do: b, typ: t }) => ({ id, od: a, do: b, typ: t }))
    : [];
  // the list under the strip: the server's 409 (a race the strip did not see) or what the strip already shows
  const zobrazeneKonflikty = konflikty ?? (predbezneKonflikty.length > 0 ? predbezneKonflikty : null);
  const zobrazenaMezera = konflikty ? mezera : (dostupnostPlatna ? dostupnost.data.mezera_dni : 0);
  // only a správce may book through a collision: for everyone else the button waits for another term
  const obsazeno = !canOverride && predbezneKonflikty.length > 0;
  // A car booked into the workshop cannot be rented over that time, and nobody may book through it (a správce included). Internal
  // blocks are not judged. The server answers the same; the strip tells before the save.
  const servisPred = !blokace && dostupnostPlatna ? servisPrekazky(dostupnost.data, rezervace ? { od: rezervace.od, do: rezervace.do } : null) : [];
  const servisBlokace = servisServer ?? (servisPred.length > 0 ? servisPred : null);

  // Every way out ends in `onOpenChange(false)`; while the save is pending it would unmount the observer its
  // toasts hang on, so it waits.
  const guarded = (next: boolean) => {
    if (!next && (busy || platbaBezi)) return;
    onOpenChange(next);
  };

  const submit = (prekrocit: boolean) => {
    if (busy) return;
    if (zacatekProblem) return void toast.error(zacatekProblem);
    if (cenaCeka) return void toast.error('Cena se právě přepočítává, uložte prosím za okamžik.');
    if (idVozidla == null) return void toast.error('Vyberte vozidlo.');
    if (idPobocky == null) return void toast.error('Vyberte pobočku.');
    if (!blokace && zakaznik == null) return void toast.error('Vyberte zákazníka.');
    if (jePojistovna && idPojistovny == null) return void toast.error('Vyberte pojišťovnu, které se rezervace účtuje.');
    const slevaVysledek = slevaZFormulare(sleva);
    if (!slevaVysledek.ok) return void toast.error(slevaVysledek.chyba);
    const kauceVysledek = kauceZFormulare(kauce);
    if (!kauceVysledek.ok) return void toast.error(kauceVysledek.chyba);
    setKonflikty(null);
    setServisServer(null);
    save.mutate(
      {
        id: rezervace?.id,
        body: {
          id_vozidla: idVozidla,
          id_pobocky: idPobocky,
          typ,
          stredisko: strediskoUlozene,
          od: fromInputValue(od),
          do: fromInputValue(do_),
          id_zakaznika: blokace ? null : zakaznik!.id,
          poznamka: poznamka.trim(),
          ...(jePojistovna ? { id_pojistovny: idPojistovny } : {}),
          ...(blokace ? {} : cenaBody(rezervace, zahranici, slevaVysledek.sleva, kauceVysledek.kauce)),
          prekrocit_konflikt: prekrocit,
        },
      },
      {
        onSuccess: () => {
          toast.ok(editing ? 'Rezervace uložena.' : 'Rezervace vytvořena.');
          onOpenChange(false);
        },
        onError: (err) => {
          const e = err as ApiError;
          const body = e.body;
          if (e.status === 409 && body && typeof body === 'object' && Array.isArray((body as { konflikty?: unknown }).konflikty)) {
            setKonflikty((body as { konflikty: RezervaceKonflikt[] }).konflikty);
            setMezera(Number((body as { mezera_dni?: number }).mezera_dni ?? 0));
            return;
          }
          if (e.status === 409 && body && typeof body === 'object' && Array.isArray((body as { servis_udalosti?: unknown }).servis_udalosti)) {
            setServisServer((body as { servis_udalosti: Array<{ id_udalosti: number; popis: string }> }).servis_udalosti);
            return;
          }
          toast.error(`Chyba: ${e.message ?? 'Nepodařilo se uložit rezervaci.'}`);
        },
      },
    );
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    submit(false);
  };

  return (
    <Modal open={open} onOpenChange={guarded} maxWidth={880}>
      <DialogCrown
        eyebrow="Rezervace"
        eyebrowMeta={rezervace ? <span data-testid="rezervace-cislo">№ {rezervace.id}</span> : undefined}
        title={editing ? (zamknuto ? <>Detail <em>rezervace</em></> : <>Upravit <em>rezervaci</em></>) : <>Nová <em>rezervace</em></>}
        sub="Vůz a zákazník se berou z vozového parku a z evidence zákazníků. Technická rezervace je interní blokace vozu; Servis vůz zablokuje pro pronájem na dobu opravy (bez zákazníka a bez předání)."
      />
      <Modal.Body className="px-8 py-5">
        <form id="rezervace-form" data-testid="rezervace-form" onSubmit={onSubmit} className="flex flex-col gap-5">
          {jenCteni && !vraceno && (
            <p data-testid="rezervace-jen-cteni" className="border border-line bg-bg px-3 py-2 text-[12px] text-ink-2">
              {jenCteni}
            </p>
          )}
          {vraceno && (
            <p data-testid="rezervace-vraceno" className="border border-line bg-bg px-3 py-2 text-[12px] text-ink-2">
              Vozidlo je vráceno — zápůjčka je ukončená a nelze ji upravit.
            </p>
          )}
          {predano && (
            <p data-testid="rezervace-predano" className="border border-line bg-bg px-3 py-2 text-[12px] text-ink-2">
              Vozidlo je předáno zákazníkovi — lze jen prodloužit konec a upravit poznámku. Zkrátit rezervaci i ukončit
              zápůjčku umí až přijetí vozidla.
            </p>
          )}
          <Fields cols={2}>
            <Field label="Vozidlo" required hint={vozidloHint} hintTone={vozidloHintTon}>
              <SelectInput
                value={idVozidla ?? ''}
                disabled={busy || zamknuto || vozidloZamceno}
                data-testid="rezervace-vozidlo"
                onChange={(e) => {
                  const id = e.currentTarget.value ? Number(e.currentTarget.value) : null;
                  setIdVozidla(id);
                  setKonflikty(null); // the collisions shown belong to the car and term that were rejected
                  setServisServer(null);
                  const car = fleet.find((f) => f.id === id);
                  // a new reservation takes the car's branch; a booking that is only moved to another car keeps the branch it has
                  if (car && !editing) setIdPobocky(car.id_pobocky);
                }}
              >
                <option value="">— vyberte —</option>
                {fleet.map((f) => (
                  <option key={f.id} value={f.id}>
                    {vozidloLabel(f)}
                  </option>
                ))}
                {/* a car that left the fleet is still the car of its reservations */}
                {rezervace && !fleet.some((f) => f.id === rezervace.id_vozidla) && (
                  <option value={rezervace.id_vozidla}>{rezervace.vin}</option>
                )}
              </SelectInput>
            </Field>
            <Field label="Pobočka" required>
              <SelectInput
                value={idPobocky ?? ''}
                disabled={zamceno}
                data-testid="rezervace-pobocka"
                onChange={(e) => setIdPobocky(e.currentTarget.value ? Number(e.currentTarget.value) : null)}
              >
                {pobocky.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nazev}
                  </option>
                ))}
              </SelectInput>
            </Field>
            <Field label="Od" required state={zacatekProblem ? 'error' : undefined} hint={zacatekProblem ?? undefined} hintTone="error">
              <TextInput
                type="datetime-local"
                value={od}
                // the picker greys out the days that have passed (a change keeps the start it already has)
                min={editing ? undefined : `${dnes}T00:00`}
                disabled={zamceno}
                data-testid="rezervace-od"
                onChange={(e) => {
                  setOd(e.currentTarget.value);
                  setKonflikty(null);
                  setServisServer(null);
                }}
              />
            </Field>
            <Field label="Do" required>
              <TextInput
                type="datetime-local"
                value={do_}
                disabled={busy || zamknuto}
                min={predano && rezervace ? toInputValue(rezervace.do) : od || undefined}
                data-testid="rezervace-do"
                onChange={(e) => {
                  setDo(e.currentTarget.value);
                  setKonflikty(null);
                  setServisServer(null);
                }}
              />
            </Field>
            <Field label="Typ zápůjčky" required>
              <SegmentedControl<RezervaceTyp>
                aria-label="Typ zápůjčky"
                value={typ}
                onChange={(v) => !zamceno && setTyp(v)}
                options={[
                  { value: 'denni', label: 'Denní' },
                  { value: 'technicky', label: 'Technický' },
                  { value: 'servis', label: 'Servis' },
                ]}
              />
            </Field>
            {!blokace && (
              <Field label="Středisko" required>
                <SelectInput
                  value={stredisko}
                  disabled={zamceno}
                  data-testid="rezervace-stredisko"
                  onChange={(e) => setStredisko(e.currentTarget.value as RezervaceStredisko)}
                >
                  {STREDISKA.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </SelectInput>
              </Field>
            )}
            {jePojistovna && (
              <Field label="Pojišťovna" required hint="Cena náhradního vozidla se řídí sazbou pojišťovny pro tarif vozu.">
                <SelectInput
                  value={idPojistovny ?? ''}
                  disabled={zamceno}
                  data-testid="rezervace-pojistovna"
                  onChange={(e) => setIdPojistovny(e.currentTarget.value === '' ? null : Number(e.currentTarget.value))}
                >
                  <option value="">— vyberte —</option>
                  {(pojistovny.data ?? []).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nazev}
                    </option>
                  ))}
                  {/* an insurer the reservation already has stays selectable while the list loads */}
                  {idPojistovny != null && !(pojistovny.data ?? []).some((p) => p.id === idPojistovny) && (
                    <option value={idPojistovny}>{rezervace?.pojistovna_nazev ?? `#${idPojistovny}`}</option>
                  )}
                </SelectInput>
              </Field>
            )}
          </Fields>

          {dostupnost.data && dostupnostNyni && !zamknuto && (
            <RezervaceDostupnost
              data={dostupnost.data}
              term={{ od: dostupnostNyni.od, do: dostupnostNyni.do }}
              today={dnes}
              nacita={!dostupnostPlatna}
            />
          )}

          {zobrazeneKonflikty && (
            <div data-testid="rezervace-konflikt" role="alert" className="flex flex-col gap-2 border border-red bg-red-soft p-4">
              <div className="text-[13px] font-medium text-red-text">Vozidlo je v tomto termínu obsazené:</div>
              {zobrazenaMezera > 0 && (
                <p data-testid="rezervace-mezera" className="text-[12px] text-ink-2">
                  Mezi vrácením a dalším převzetím musí uplynout alespoň {zobrazenaMezera} {zobrazenaMezera === 1 ? 'den' : zobrazenaMezera < 5 ? 'dny' : 'dní'}
                  {' '}— rezervace mohou kolidovat i bez překryvu termínů.
                </p>
              )}
              <ul className="flex flex-col gap-1 text-[13px] text-ink">
                {zobrazeneKonflikty.map((k) => (
                  <li key={k.id} className="font-mono">
                    {formatTermin(k.od, k.do)}
                    {k.typ === 'technicky' ? ' · technická' : k.typ === 'servis' ? ' · servis' : ''}
                  </li>
                ))}
              </ul>
              {canOverride ? (
                <div>
                  <Button size="sm" variant="secondary" type="button" disabled={busy || cenaCeka} onClick={() => submit(true)}>
                    Rezervovat přesto
                  </Button>
                </div>
              ) : (
                <p className="text-[12px] text-ink-2">Rezervaci přes obsazený termín může zadat jen správce půjčovny.</p>
              )}
            </div>
          )}

          {servisBlokace && (
            <div data-testid="rezervace-servis" role="alert" className="flex flex-col gap-2 border border-red bg-red-soft p-4">
              <div className="text-[13px] font-medium text-red-text">Vozidlo je v tomto termínu v servisu:</div>
              <ul className="flex flex-col gap-1 text-[13px] text-ink">
                {servisBlokace.map((k) => (
                  <li key={k.id_udalosti} className="font-mono">{k.popis}</li>
                ))}
              </ul>
              <p className="text-[12px] text-ink-2">Na dobu servisu nelze vozidlo pronajmout — zvolte jiný termín.</p>
            </div>
          )}

          {!blokace && (
            <section data-testid="rezervace-zakaznik" className="flex flex-col gap-2 border border-line p-4">
              <div className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">Zákazník</div>
              {zakaznik ? (
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <span className="text-[14px] text-ink" data-testid="rezervace-zakaznik-vybrany">
                    {zakaznik.nazev}
                  </span>
                  <Button size="sm" variant="secondary" type="button" disabled={zamceno} onClick={() => setZakaznik(null)}>
                    Změnit
                  </Button>
                </div>
              ) : (
                <>
                  <SearchInput
                    value={search}
                    placeholder="Hledat zákazníka…"
                    disabled={busy}
                    onChange={(e) => setSearch(e.currentTarget.value)}
                  />
                  <ul className="border border-line">
                    {customers.customers.length === 0 ? (
                      <li className="px-3 py-2 text-[12px] text-ink-3">
                        {customers.query.isLoading ? 'Načítám…' : debounced ? 'Nikdo nenalezen.' : 'Začněte psát jméno, IČ nebo e-mail.'}
                      </li>
                    ) : (
                      customers.customers.map((c) => (
                        <li key={c.id_zakaznika} className="border-b border-line last:border-b-0">
                          <button
                            type="button"
                            disabled={busy}
                            data-testid={`rezervace-zakaznik-${c.id_zakaznika}`}
                            onClick={() => setZakaznik({ id: c.id_zakaznika, nazev: c.jmeno || `#${c.id_zakaznika}` })}
                            className="flex w-full flex-wrap items-baseline justify-between gap-x-4 px-3 py-2 text-left hover:bg-accent-soft disabled:opacity-50"
                          >
                            <span className="text-[14px] text-ink">{c.jmeno || '—'}</span>
                            <span className="font-mono text-[11px] text-ink-3">{[c.ico, c.mesto].filter(Boolean).join(' · ')}</span>
                          </button>
                        </li>
                      ))
                    )}
                  </ul>
                  {customers.remaining > 0 && (
                    <div>
                      <Button size="sm" variant="secondary" type="button" disabled={busy} onClick={customers.loadMore}>
                        Načíst další ({customers.remaining})
                      </Button>
                    </div>
                  )}
                  {smiZalozitZakaznika && (
                    <p data-testid="rezervace-novy-zakaznik" className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-ink-3">
                      <LinkButton as="a" href="/zakaznici/novy" target="_blank" rel="noreferrer">
                        Založit nového zákazníka
                      </LinkButton>
                      <span>Otevře se v novém okně; po uložení se sem vraťte a zákazníka vyhledejte.</span>
                      {debounced && (
                        <LinkButton onClick={() => void customers.query.refetch()} data-testid="rezervace-hledat-znovu">
                          Hledat znovu
                        </LinkButton>
                      )}
                    </p>
                  )}
                </>
              )}
            </section>
          )}

          {!blokace && (
            <CenaPanel
              rozpis={cenaBezeZmeny ? rezervace!.cena : (dotazZastaral ? null : (nabidka.data?.cena ?? null))}
              ulozena={cenaBezeZmeny}
              nacita={nabidka.isFetching || (dotazZastaral && !cenaBezeZmeny && !bezCenyPoPredani && !nechanoBezCeny)}
              chyba={nabidka.isError}
              duvod={dotazZastaral && !cenaBezeZmeny ? null : bezCenyPoPredani ? 'Vozidlo bylo předáno ještě před zavedením cen — u tohoto pronájmu se cena neurčuje.' : nechanoBezCeny ? 'Při uložení rezervace nebylo možné cenu určit. Změňte termín, zákazníka nebo slevu a cena se vypočte znovu.' : (nabidka.data?.duvod ?? (slevaParsed.ok ? null : slevaParsed.chyba))}
              upozorneni={hradiServis ? 'Pronájem hradí servis — zákazník neplatí nájemné, jen kauci.' : dosudBezCeny ? 'Rezervace zatím nemá sjednanou cenu — uložením se stanoví podle aktuálního ceníku.' : null}
              zahranici={zahranici}
              onZahranici={setZahranici}
              sleva={sleva}
              onSleva={setSleva}
              kauce={kauce}
              onKauce={setKauce}
              kauceVozu={fleet.find((f) => f.id === idVozidla)?.kauce ?? null}
              kauceChyba={kauceParsed.ok ? null : kauceParsed.chyba}
              disabled={busy || zamceno}
              bezNajemneho={hradiServis}
            />
          )}

          <Fields cols={1}>
            <Field label="Poznámka" optional="interní, volitelné">
              <TextInput
                value={poznamka}
                maxLength={500}
                disabled={busy || zamknuto}
                onChange={(e) => setPoznamka(e.currentTarget.value)}
              />
            </Field>
          </Fields>

        </form>
        {/* the money of a saved rental: its own calls, not part of the form's save */}
        {rezervace && !jeBlokace(rezervace.typ) && (
          <div className="mt-5">
            <PlatbyPanel rezervace={rezervace} canWrite={access.can('pujcovna', 'write')} canAdmin={canOverride} onBusy={setPlatbaBezi} />
          </div>
        )}
      </Modal.Body>
      <Modal.Foot
        meta={(onPredat || onPrijmout) && rozepsano ? 'Předání a přijetí vozu lze až po uložení změn.' : undefined}
        actions={
          <>
            <Button variant="secondary" onClick={() => guarded(false)} disabled={busy}>
              {zamknuto ? 'Zavřít' : 'Zrušit'}
            </Button>
            {/* the preview prints the SAVED reservation, so — like the handover — not over unsaved edits */}
            {rezervace && !rozepsano && !rezervace.predano_at && rezervace.stav === 'aktivni' && rezervace.typ === 'denni' && (
              <span className="mr-auto flex items-center gap-x-3" data-testid="rezervace-tisk">
                <Link
                  to="/pujcovna/rezervace/$id/smlouva"
                  params={{ id: String(rezervace.id) }}
                  className={LINK_BUTTON_CLS}
                  title="Náhled před předáním — údaje se zmrazí až s předáním."
                >
                  Tisk smlouvy
                </Link>
              </span>
            )}
            {rezervace?.predano_at && (
              <span className="mr-auto flex items-center gap-x-3" data-testid="rezervace-tisk">
                {!jeBlokace(rezervace.typ) && (
                  <Link to="/pujcovna/rezervace/$id/smlouva" params={{ id: String(rezervace.id) }} className={LINK_BUTTON_CLS}>
                    Tisk smlouvy
                  </Link>
                )}
                <Link to="/pujcovna/rezervace/$id/protokol" params={{ id: String(rezervace.id) }} className={LINK_BUTTON_CLS}>
                  Tisk protokolu
                </Link>
              </span>
            )}
            {onPredat && (
              <Button
                variant="secondary"
                disabled={busy || rozepsano}
                title={rozepsano ? 'Nejdřív uložte změny rezervace.' : undefined}
                data-testid="rezervace-predat"
                onClick={onPredat}
              >
                Předat vůz…
              </Button>
            )}
            {onPrijmout && (
              <Button
                variant="secondary"
                disabled={busy || rozepsano}
                title={rozepsano ? 'Nejdřív uložte změny rezervace.' : undefined}
                data-testid="rezervace-prijmout"
                onClick={onPrijmout}
              >
                Přijmout vozidlo…
              </Button>
            )}
            {!zamknuto && (
              <Button
                variant="ink"
                type="submit"
                form="rezervace-form"
                disabled={busy || cenaCeka || zacatekProblem != null || obsazeno || servisBlokace != null}
                title={servisBlokace ? 'Vozidlo je v tomto termínu v servisu — zvolte jiný termín.' : obsazeno ? 'Vozidlo je v tomto termínu obsazené — zvolte jiný termín.' : undefined}
              >
                {editing ? 'Uložit' : 'Vytvořit rezervaci'}
              </Button>
            )}
          </>
        }
      />
    </Modal>
  );
}
